// ClaraUseCasePage.jsx
// Parallel to UseCasePage.jsx — for Clara DB data (Global/ESI "Integration Domains for Global Products").
//
// Current state (Step 2): Structural clone of UseCasePage using the same CMS data source.
// TODO (Step 3): Replace useFetchElasticSearchByIdQuery with Clara API hook.
// TODO (Step 4): Adapt field mappings once Clara API response shape is confirmed.

import React, { useEffect, useState } from "react";
import financeImg from "../../../assets/illustrations/finance-img.png";
import get from "lodash/get";
import {
  SdfButton,
  SdfTooltip,
  SdfIcon,
  SdfAlert,
  SdfBox,
} from "@synerg/react-components";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import ClaraFeatureSteps from "./ClaraFeatureSteps";
import {
  useFetchElasticSearchByIdQuery,
  useFetchClaraDomainUseCasesQuery,
  useFetchUseCaseApisSorQuery,
  useUpdateMetricsMutation,
  claraApi,
} from "../../../store";
import { buildQueryObj } from "../../../utility/FetchingUtils";
import { NotFound } from "../../NotFound";
import Breadcrumb from "../../../components/Breadcrumb";
import { closeAppBarSearch } from "../../../utility/searchUtil";
import { BusyPageIndicator } from "../../../components/BusyPageIndicator";
import "./UseCasePage.css";

const integrationFlowsStateHash = "#integration-flow";
const iconRegex = /^[a-z]+(-[a-z]+)*$/;

// Maps Clara domain names to the single useCaseTags value used in the international API guides index.
// Domains not listed here have no unique tag in the guides index and fall back to showing all guides.
const CLARA_DOMAIN_TO_GUIDE_TAGS = {
  "Finance":           "Finance",
  "Time & Attendance": "Time & Activities Management",
  "Employee Engagement": "Engagement & Benefits",
};

function ClaraUseCasePage({ id, searchBar, showBreadcrumb = true }) {
  // Helper component to render SoR names in tooltip from /sor API response
  function SorTooltipContent({ useCaseId }) {
    const { data: sorData = [], isFetching, isLoading } = useFetchUseCaseApisSorQuery(useCaseId, {
      skip: !useCaseId,
    });

    // Extract unique sorNames — only SORs explicitly linked to this use case via use_case_sor.
    // sorData may include extra sor_api rows (LEFT JOIN) used for guide links; those are excluded here.
    const sorNames = Array.from(
      new Set(
        sorData
          ?.filter((item) => item?.mappedToUseCase)
          ?.map((item) => item?.sorName)
          ?.filter(Boolean)
      )
    ).sort();

    return (
      <SdfTooltip
        tooltipId={`compatible-adp-products-${useCaseId}`}
        attachmentPoint="right"
        trigger="hover"
      >
        <SdfButton
          icon-only=""
          icon="alert-info"
          variant="text"
          slot="tooltip-target"
        />
        {isFetching || isLoading ? (
          <div className="flex items-center gap-2 text-body-02">
            <SdfIcon icon="loading" className="animate-spin text-sm" />
            <span>Loading...</span>
          </div>
        ) : sorNames?.length > 0 ? (
          sorNames.map((sorName) => (
            <div key={sorName} className="text-body-02">{sorName}</div>
          ))
        ) : (
          <div className="text-body-02">No compatible ADP products available</div>
        )}
      </SdfTooltip>
    );
  }

  // Prefetch hooks — fire on card hover to give Databricks a head start before the click.
  const prefetchApis = claraApi.usePrefetch('fetchUseCaseApis', { ifOlderThan: 60 });
  const prefetchSor  = claraApi.usePrefetch('fetchUseCaseApisSor', { ifOlderThan: 60 });

  const [showFeatureSteps, setShowFeatureSteps] = useState(false);
  const [selectedFeatureItem, setSelectedFeatureItem] = useState({});
  const { id: paramsId } = useParams();
  const location = useLocation();

  if (!id) {
    id = paramsId;
  }

  // Validate id is a safe UUID/slug before using in navigation paths
  const safeId = id && /^[a-zA-Z0-9_-]+$/.test(id) ? id : null;

  // This page is only ever reached via /solutions/clara/:id — always use the Databricks lambda.
  const {
    isSuccess,
    data: source,
    isFetching,
  } = useFetchClaraDomainUseCasesQuery(id, { skip: !id });

  // ElasticSearch hook retained only to satisfy hook call-order rules — never executes.
  useFetchElasticSearchByIdQuery(
    buildQueryObj({ index: "usecases", id, sort: [{}] }),
    { skip: true }
  );

  // TODO (Step 4): Update field paths if Clara data shape differs
  const section1 = get(source, "content.section1", "");
  const solutions = get(source, "content.section2.solutions", []);

  const navigate = useNavigate();
  const solutionTitle = get(source, "title", "");

  const breadcrumbPath = [
    { name: "Home", route: "/" },
    { name: "Solutions" },
    { name: solutionTitle },
  ];

  const [updateMetrics, results] = useUpdateMetricsMutation();

  useEffect(() => {
    // Clara domain UUIDs are never in Elasticsearch — skip metrics update entirely.
  }, []);

  const errorMsg = results.error ? (
    <SdfAlert closeable status="error">
      An error occurred when updating metrics for this document
    </SdfAlert>
  ) : (
    <></>
  );

  const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  const updateFeatureFromHash = () => {
    const hash = location.hash;
    if (hash.startsWith('#use-case-')) {
      const useCaseId = hash.replace('#use-case-', '');
      if (!UUID_REGEX.test(useCaseId)) return;
      const match = solutions.find((s) => s.useCaseId === useCaseId);
      if (!match) {
        setShowFeatureSteps(false);
        setSelectedFeatureItem({});
      } else {
        setShowFeatureSteps(true);
        setSelectedFeatureItem(match);
      }
    }
  };

  useEffect(() => {
    updateFeatureFromHash();
    if (showFeatureSteps && location.hash === "") {
      setShowFeatureSteps(false);
      setSelectedFeatureItem({});
    }
  }, [location]);

  useEffect(() => {
    updateFeatureFromHash();
  }, [source]);

  const onOpenHandler = (solution) => {
    console.log('ClaraUseCasePage "View Associated APIs" clicked');
    closeAppBarSearch();
    navigate({
      pathname: `/solutions/clara/${safeId}`,
      hash: `use-case-${solution.useCaseId}`,
    });
    setShowFeatureSteps(true);
    setSelectedFeatureItem(solution);
  };

  const onCloseHandler = () => {
    navigate(`/solutions/clara/${safeId}`);
    setShowFeatureSteps(false);
    setSelectedFeatureItem({});
  };

  const clickHandler = () => {
    const tag = CLARA_DOMAIN_TO_GUIDE_TAGS[solutionTitle];
    closeAppBarSearch();
    if (!tag) {
      // No unique tag for this domain — navigate to all guides with the domain title as search query
      const params = new URLSearchParams({ filterQuery: solutionTitle || "", product: "" });
      navigate({ pathname: "/guides/api-guides", search: params.toString() });
      return;
    }
    const params = new URLSearchParams({ filterQuery: "", product: "", useCaseTags: tag });
    navigate({ pathname: "/guides/api-guides", search: params.toString() });
  };

  if (!isFetching && solutions?.length === 0) return <NotFound />;

  return isFetching ? (
    <BusyPageIndicator positionClass="absolute-center" />
  ) : (
    <div className="min-h-screen">
      <div className="w-11/12 m-auto">
        {showBreadcrumb && <Breadcrumb path={breadcrumbPath} />}
        {errorMsg}
      </div>
      <div
        className={`m-auto bg-action-primary-900 rounded-lg${
          searchBar ? "" : " w-11/12"
        }`}
      >
        <div
          className={`flex flex-col justify-between gap-8${
            searchBar ? " px-4 py-8" : " p-8 lg:flex-row lg:gap-16"
          }`}
        >
          <div className="flex flex-col justify-between py-4">
            <div className="flex flex-col gap-4">
              <h1 className="font-black text-inverse-default m-0 text-heading-05">
                {"International solutions"}
              </h1>
              <h2 className="font-black text-inverse-default m-0 text-heading-07">
                {section1?.title}
              </h2>
              <p className="font-black text-inverse-default m-0 text-heading-03">
                {section1?.subtitle}
              </p>
            </div>
          </div>
          <img
              className="h-xs w-xs m-auto lg:m-0 lg:mr-16 object-contain"
              src={section1?.img || financeImg}
              alt=""
            />
        </div>
      </div>
      <div className="m-auto pt-8 pb-16 w-11/12">
        <ul
          className={`list-none m-0 p-0 grid grid-cols-1${
            searchBar
              ? " px-4"
              : "pl-0 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8"
          }`}
        >
          {solutions?.map((solution, index) => (
            <li
              className="flex flex-col"
              key={index}
              onMouseEnter={() => {
                if (solution?.useCaseId) {
                  prefetchApis(solution.useCaseId);
                  prefetchSor(solution.useCaseId);
                }
              }}
            >
              <div className="flex-grow flex-shrink-0">
                <SdfBox className="h-full" variant="bordered">
                  <div className="h-full flex flex-col justify-between gap-4">
                    <div className="flex flex-col gap-4">
                      <div className="relative flex items-center justify-center solution-image-container">
                        <div className="bg-action-help-700 w-full h-full solution-image-shape" />
                        <SdfIcon
                          icon="action-view-reports"
                          className="absolute text-4xl text-inverse-default m-auto w-full h-full"
                        />
                      </div>
                      <h3 className="m-0 text-heading-05">{solution?.iconTitle}</h3>
                      <p className="m-0 text-body-02">{solution?.iconSubtitle}</p>
                    </div>
                    <div>
                      {solution?.apiCount > 0 && (
                        <SdfButton
                          emphasis="primary"
                          onclick={() => onOpenHandler(solution)}
                        >
                          View associated APIs
                        </SdfButton>
                      )}
                    </div>
                  </div>
                </SdfBox>
              </div>
              <div className="use-case-compatible">
                {solution?.apiCount > 0 && (
                  <>
                    <SorTooltipContent useCaseId={solution?.useCaseId} />
                    <span className="ml-2 text-default">
                      Compatible ADP Products
                    </span>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>

      </div>

      {showFeatureSteps && (
        <ClaraFeatureSteps
          handleOnHide={onCloseHandler}
          show={showFeatureSteps}
          selectedFeatureItem={selectedFeatureItem}
          useCaseInfo={section1}
        />
      )}
    </div>
  );
}

export default ClaraUseCasePage;
